import axios from "axios";
import router from "../router";

// set defaults
axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.withCredentials = true;

//set interceptor for adding cache busting
axios.interceptors.request.use(
  (config) => {
    config.url += `?r=${[...Array(10)]
      // eslint-disable-next-line
      .map((i) => (~~(Math.random() * 36)).toString(36))
      .join("")}`;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const initialState = () => ({
  hostedCheckoutId: "",
  page: "",
  pageDetails: {},
  orderDetails: {
    cartName: "",
    currency: "",
    amount: 0,
  },
  merchantDetails: {},
  styling: {},
  language: process.env.VUE_APP_I18N_FALLBACK_LOCALE,
  loading: false,
  overlayBox: false,
  autoSelectSinglePaymentMethod: true,
});

const getBrowserInfo = () => ({
  java_enabled: navigator.javaEnabled(),
  /*always enabled*/
  java_script_enabled: true,
  language: navigator.language,
  color_depth: screen.colorDepth,
  screen_height: screen.height,
  screen_width: screen.width,
  time_zone_offset: new Date().getTimezoneOffset(),
  challenge_window_size: "05",
});

const createCustomElement = function (type, props) {
  let element = document.createElement(type);

  for (let prop in props) {
    element.setAttribute(prop, props[prop]);
  }

  return element;
};

export default {
  state: initialState,
  getters: {
    hostedCheckoutId: (state) => state.hostedCheckoutId,
    page: (state) => state.page,
    pageDetails: (state) => state.pageDetails,
    orderDetails: (state) => state.orderDetails,
    merchantDetails: (state) => state.merchantDetails,
    styling: (state) => state.styling,
    language: (state) => state.language,
    loading: (state) => state.loading,
    overlayBox: (state) => state.overlayBox,
    backgroundUrl: (state) => {
      if (!state.styling || !state.styling.backgroundImageUrl) {
        return;
      }
      return "url('" + state.styling.backgroundImageUrl + "')";
    },
    logoUrl: (state) => {
      if (!state.styling || !state.styling.logoImageUrl) {
        return;
      }
      return "url('" + state.styling.logoImageUrl + "')";
    },
    cssUrl: (state) => {
      if (!state.styling || !state.styling.cssUrl) {
        return;
      }
      return state.styling.cssUrl;
    },
    autoSelectSinglePaymentMethod: (state) =>
      state.autoSelectSinglePaymentMethod,
  },
  mutations: {
    setHostedCheckoutId(state, id) {
      state.hostedCheckoutId = id;
    },
    setTerminalId(state, id) {
      state.pageDetails.terminalId = id;
    },
    setErrorCode(state, code) {
      state.pageDetails.errorCode = code;
    },
    setState(state, data) {
      state.page = data.page;
      state.pageDetails = data.pageDetails;
      state.orderDetails = data.orderDetails;
      state.merchantDetails = data.merchantDetails;
      state.styling = data.styling;
      state.language = data.language;
    },
    setLoading(state, status) {
      state.loading = status;
    },
    setOverlayBox(state, status) {
      state.overlayBox = status;
    },
    resetState(state) {
      const initial = initialState();
      Object.keys(initial).forEach((key) => {
        state[key] = initial[key];
      });
    },
    setAutoSelectSinglePaymentMethod(state, status) {
      state.autoSelectSinglePaymentMethod = status;
    },
  },
  actions: {
    setOverlayBox({ commit }, status) {
      commit("setOverlayBox", status);
    },
    getState({ commit, state }) {
      commit("setLoading", true);
      commit("setOverlayBox", true);
      return axios
        .get(`/get-state/${state.hostedCheckoutId}`)
        .then((result) => {
          commit("setLoading", false);
          commit("setOverlayBox", false);
          commit("setState", result.data);
          return result.data;
        })
        .catch((error) => {
          commit("setLoading", false);
          if (error.response.status === 404) {
            router.push({ name: "payment-session-expired" });
          } else {
            router.push({ name: "payment-failure" });
          }
        });
    },
    getPollingState({ commit, state }) {
      return axios
        .get(`/get-state/${state.hostedCheckoutId}`)
        .then((result) => {
          return result.data;
        })
        .catch((error) => {
          commit("setLoading", false);
          if (error.response.status === 404) {
            router.push({ name: "payment-session-expired" });
          } else {
            router.push({ name: "payment-failure" });
          }
        });
    },
    updateState({ commit, state }, params) {
      commit("setLoading", true);
      return axios
        .post(
          `/update-state/${state.hostedCheckoutId}/${params.path}`,
          params.data
        )
        .then((result) => {
          switch (result.data.page) {
            case "redirect-to-3rd-party":
            case "payment-failure-merchant":
            case "payment-success":
              for (let redirect of result.data.pageDetails.redirects) {
                var method = redirect.method || "GET";
                var purpose = redirect.purpose;
                //  For GET redirects simply update the location
                //  For POST redirects, create a form and contains the required data
                // For HIDDEN_IFRAME, POST the url to the iframe
                // for IFRAME, POST to the url. Response contains an IFRAME with a challenge from a third party
                if (method.toUpperCase() === "GET") {
                  window.location.href = redirect.url;
                } else {
                  switch (purpose) {
                    case "REDIRECT":
                      var form = createCustomElement("form", {
                        method: method,
                        action: redirect.url,
                      });

                      document.body.appendChild(form);

                      for (var param of redirect.payload.parameter) {
                        var input = createCustomElement("input", {
                          type: "hidden",
                          name: param.name,
                          value: param._,
                        });

                        form.appendChild(input);
                      }

                      form.submit();

                      break;

                    case "HIDDEN_IFRAME": // Invisible IFRAME to get user/browser data
                      var hiddenIframe = createCustomElement("iframe", {
                        width: 1,
                        height: 1,
                        name: "hiddenIframe",
                      });

                      document.body.appendChild(hiddenIframe);

                      var hiddenForm = createCustomElement("form", {
                        method: method,
                        action: redirect.url,
                        target: hiddenIframe.name,
                      });

                      document.body.appendChild(hiddenForm);

                      var hiddenInput = createCustomElement("input", {
                        type: "hidden",
                        name: redirect.payload.parameter.name,
                        value: redirect.payload.parameter._,
                      });

                      hiddenForm.appendChild(hiddenInput);

                      // POST the request to the iFrame
                      hiddenForm.submit();
                      break;

                    case "IFRAME":
                      // IFRAME can contain a third party challenge to verify your identity
                      // in all other cases (SOFT_DECLINE, AUTHORIZED, ERROR, CANCELED) reload the page and log the response result
                      var data = {
                        browser_info: getBrowserInfo(),
                      };

                      // Toggle the iframe screen in card.vue component
                      if (params.callbackToggle) params.callbackToggle();

                      // use iframe container width/height when provided
                      if (params.iframeHolder) {
                        data.browser_info.screen_height =
                          params.iframeHolder.offsetHeight > 400
                            ? params.iframeHolder.offsetHeight
                            : 400;
                        data.browser_info.screen_width =
                          params.iframeHolder.offsetWidth;
                      }

                      axios
                        .post(redirect.url, data, {
                          withCredentials: false,
                          headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                          },
                        })
                        .then((res) => {
                          if (res.status === 200) {
                            let response = res.data;
                            switch (response.result) {
                              case "CHALLENGE":
                                var challengeIframe = createCustomElement(
                                  "iframe",
                                  {
                                    name: "challenge",
                                    height: "100%",
                                    width: "100%",
                                  }
                                );

                                for (let responseUrl of response.urls) {
                                  var challengeForm = createCustomElement(
                                    "form",
                                    {
                                      method: responseUrl.method,
                                      action: responseUrl.url,
                                      target: challengeIframe.name,
                                    }
                                  );

                                  document.body.appendChild(challengeForm);

                                  for (let responseUrlParams in responseUrl.parameters) {
                                    var challengeInput = createCustomElement(
                                      "input",
                                      {
                                        type: "hidden",
                                        name: responseUrlParams,
                                        value:
                                          responseUrl.parameters[
                                            responseUrlParams
                                          ],
                                      }
                                    );

                                    challengeForm.appendChild(challengeInput);
                                  }
                                }

                                params.iframeHolder.appendChild(
                                  challengeIframe
                                );
                                challengeForm.submit();

                                // Because we can not check the users' input in the iframe, poll the payment status for any final state.
                                var poll = function () {
                                  axios
                                    .get(
                                      `/get-state/${state.hostedCheckoutId}`,
                                      params.data
                                    )
                                    .then((result) => {
                                      switch (result.data.page) {
                                        case "payment-failure-merchant":
                                        case "payment-success":
                                          window.location.href =
                                            result.data.pageDetails.url;
                                          break;

                                        case "payment-failure":
                                        case "payment-session-expired":
                                          router.push({
                                            path: `/${result.data.page}`,
                                          });
                                          break;

                                        default:
                                          setTimeout(poll, 2000);
                                          break;
                                      }
                                    });
                                };

                                // Start polling the payment status.
                                setTimeout(poll, 5000);

                                break;

                              case "SOFT_DECLINE":
                              case "AUTHORIZED":
                                console.debug(
                                  `IFRAME resulted in: ${response.result}`
                                );
                                location.reload();
                                break;

                              case "ERROR":
                              case "CANCELED":
                              default:
                                commit("setLoading", false);
                                commit("setState", result.data);
                                console.debug(
                                  `IFRAME resulted in: ${response.result}`
                                );
                                router.push({ path: `/${result.data.page}` });
                            }
                          }
                        });

                      commit("setLoading", false);

                      break;

                    default:
                      commit("setLoading", false);
                      commit("setState", result.data);
                      router.push({ path: `/${result.data.page}` });
                  }
                }
              }

              break;
            default:
              commit("setLoading", false);
              commit("setState", result.data);
              router.push({ path: `/${result.data.page}` });
          }
        })
        .catch(() => {
          commit("setLoading", false);
          router.push({ name: "payment-failure" });
        });
    },
    async restart({ commit, state }) {
      commit("setLoading", true);
      return axios
        .post(`/update-state/${state.hostedCheckoutId}/restart`)
        .then((result) => {
          router.push({ path: `/${result.data.page}` });
          switch (result.data.page) {
            case "redirect-to-3rd-party":
              window.location.href = result.data.pageDetails.url;
              break;
            case "payment-failure-merchant":
              window.location.href = result.data.pageDetails.url;
              break;
            case "payment-success":
              window.location.href = result.data.pageDetails.url;
              break;
            default:
              commit("setLoading", false);
              commit("setState", result.data);
              return result.data;
          }
        })
        .catch(() => {
          commit("setLoading", false);
          router.push({ name: "payment-failure" });
        });
    },
    setLanguage({ state }, params) {
      return axios.post(
        `/update-state/${state.hostedCheckoutId}/set-language`,
        params
      );
    },
  },
};
